import { PageSpinner, useNav } from '@armhr/ui';
import { ExpandLess, ExpandMore, Info } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import { useCompany } from '../../contexts/company.context';
import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import { normalizeQuillHtml } from '../../utils/benefits';
import { paths } from '../../utils/paths';

const isForbidden = (err: Error | null) =>
  (err as any)?.response?.status === 403;

const SwipeclockPtoPage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { setFluid } = useNav();
  const company = useCompany();

  const [ptoDescriptionContent, setPtoDescriptionContent] =
    useState<string>('');
  const [showPtoDescriptions, setShowPtoDescriptions] =
    useState<boolean>(false);
  const [expandDescription, setExpandDescription] = useState<boolean>(true);

  const {
    data: ptoUrl,
    loading: ptoLoading,
    error: ptoError,
  } = useApiData<string | null>((api) => api.attendance.getPtoRedirect());

  // Render edge-to-edge so the iframe can own the full content height (no padded
  // container or footer below it).
  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  // Get PTO descriptions from company config
  useEffect(() => {
    const ptoDescFlag = company.config?.find(
      (config) => config.flag === 'show_pto_descriptions'
    );

    if (ptoDescFlag?.value && ptoDescFlag?.data?.content) {
      setShowPtoDescriptions(true);
      setPtoDescriptionContent(ptoDescFlag.data.content);
    }
  }, [company.config]);

  const toggleDescriptionExpand = () => {
    setExpandDescription(!expandDescription);
  };

  // The SC_PTO widget is always available when SwipeClock is on.
  if (!user?.is_swipeclock_enabled) {
    navigate(paths.dashboard);
    return null;
  }

  if (ptoLoading) {
    return <PageSpinner />;
  }

  if (isForbidden(ptoError)) {
    navigate(paths.dashboard);
    return null;
  }

  if (ptoError || !ptoUrl) {
    return (
      <Alert severity="error">
        An error occured while loading the PTO page. Please refresh and try
        again
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Helmet>
        <title>Time off | Armhr</title>
        <meta name="description" content="Request and manage your PTO." />
      </Helmet>
      <Box sx={{ display: 'flex', px: 3, py: 2, alignItems: 'center' }}>
        <Typography variant="h2">Time off</Typography>
      </Box>
      {showPtoDescriptions && ptoDescriptionContent && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Alert
            severity="info"
            icon={<Info />}
            sx={{
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
            action={
              <IconButton
                aria-label="toggle description"
                color="inherit"
                size="small"
                onClick={toggleDescriptionExpand}
              >
                {expandDescription ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            }
          >
            <AlertTitle>
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                PTO Policy Information
              </Typography>
            </AlertTitle>
            <Collapse in={expandDescription}>
              <Box
                dangerouslySetInnerHTML={{
                  __html: normalizeQuillHtml(ptoDescriptionContent),
                }}
                sx={{
                  '& a': { color: 'primary.main' },
                  '& ul': {
                    paddingLeft: '40px',
                    listStyleType: 'disc',
                    margin: '8px 0',
                  },
                  '& ol': {
                    paddingLeft: '40px',
                    listStyleType: 'decimal',
                    margin: '8px 0',
                  },
                  '& li': {
                    display: 'list-item',
                    margin: '4px 0',
                  },
                  mt: 1,
                }}
              />
            </Collapse>
          </Alert>
        </Box>
      )}
      <iframe
        src={ptoUrl}
        title="SwipeClock PTO"
        loading="lazy"
        style={{
          background: 'white',
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
    </Box>
  );
};

export default SwipeclockPtoPage;
